'use client';

import { FeatureFlagKeys } from '@/types/featureFlags';
import { useFeatureFlag as useFeatureFlagBase } from '@liveauctioneers/hammer-ui-core/featureFlags/hooks';

/**
 * Strongly-typed hook to check if a specific feature flag is enabled
 *
 * This wraps the Hammer UI useFeatureFlag hook with type safety,
 * ensuring only valid feature flag keys from the FeatureFlagKeys enum can be used.
 *
 * @param flag - The feature flag key from FeatureFlagKeys enum
 * @returns `true` if the feature flag exists and is enabled, `false` otherwise
 *
 * @example
 * ```typescript
 * import { useFeatureFlag } from '@/hooks/useFeatureFlag';
 * import { FeatureFlagKeys } from '@/types/featureFlags';
 *
 * export const MyComponent = () => {
 *   const hasNewLotMetadata = useFeatureFlag(FeatureFlagKeys.NEW_LOT_METADATA);
 *
 *   if (hasNewLotMetadata) {
 *     return <NewLotMetadataUI />;
 *   }
 *
 *   return <LegacyLotMetadataUI />;
 * };
 * ```
 */
export const useFeatureFlag = (flag: FeatureFlagKeys): boolean => {
    return useFeatureFlagBase(flag);
};
